import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuCalendarDays, LuChevronRight, LuSearchX, LuUserPlus, LuUsers } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import ColumnFilterButton from '../../../components/data/ColumnFilterButton';
import { useTranslation } from '../../../i18n/LanguageContext';
import WorkerStatusBadge from './WorkerStatusBadge';
import { initialsOf } from './WorkerDetailCard';
import { ROLE_OPTIONS, formatAssignedDate, roleLabel } from './workerOptions';

const HEAD = 'tw-sticky tw-top-0 tw-z-[1] tw-min-w-0 tw-whitespace-nowrap tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-py-2 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 dark:tw-border-white/10 dark:tw-bg-[#262a34] dark:tw-text-slate-400';
const CELL = 'tw-max-w-[14rem] tw-truncate tw-whitespace-nowrap tw-border-b tw-border-slate-100 tw-px-3 tw-py-2.5 tw-align-middle dark:tw-border-white/[0.06]';

export const WORKER_FILTER_KEYS = ['name', 'surname', 'role', 'center', 'assignedAt', 'status'];

/** Sütun filtrelerinin tanımı (tablo başlıkları ve dar ekrandaki filtre paneli için). */
export const workerFilterFields = (t) => [
  { key: 'name', label: t('projectManagement.workers.columns.name'), type: 'text' },
  { key: 'surname', label: t('projectManagement.workers.columns.surname'), type: 'text' },
  { key: 'role', label: t('projectManagement.workers.columns.roles'), type: 'select', options: ROLE_OPTIONS.map((role) => ({ value: role.name, label: roleLabel(t, role.name) })) },
  { key: 'center', label: t('projectManagement.workers.columns.centers'), type: 'text' },
  { key: 'assignedAt', label: t('projectManagement.workers.columns.assignedAt'), type: 'text' },
  {
    key: 'status',
    label: t('projectManagement.workers.columns.status'),
    type: 'select',
    options: ['active', 'passive', 'left'].map((status) => ({ value: status, label: t(`projectManagement.workers.status.${status}`) })),
  },
];

const RolesSummary = ({ roles }) => {
  const { t } = useTranslation();
  if (roles.length === 0) return <span className="tw-text-slate-400">—</span>;
  return (
    <span className="tw-inline-flex tw-max-w-full tw-items-center tw-gap-1.5" title={roles.map((role) => roleLabel(t, role)).join(', ')}>
      <span className="tw-truncate tw-rounded-full tw-bg-violet-500/10 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-violet-800 dark:tw-text-violet-200">{roleLabel(t, roles[0])}</span>
      {roles.length > 1 && <span className="tw-shrink-0 tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{`+${roles.length - 1}`}</span>}
    </span>
  );
};

RolesSummary.propTypes = { roles: PropTypes.arrayOf(PropTypes.string).isRequired };

const CentersSummary = ({ summary }) => {
  const { t } = useTranslation();
  const leftCount = summary.archivedByCenter.length;
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
      <span className="tw-tabular-nums">{t('projectManagement.workers.centerCount', { count: summary.activeCenterIds.size })}</span>
      {leftCount > 0 && (
        <span title={t('projectManagement.workers.leftHint')} className="tw-rounded-full tw-bg-amber-500/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-amber-700 dark:tw-text-amber-300">
          {t('projectManagement.workers.leftCount', { count: leftCount })}
        </span>
      )}
    </span>
  );
};

CentersSummary.propTypes = { summary: PropTypes.object.isRequired };

/**
 * Projedeki çalışanlar: geniş alanda sütun filtreli tablo, dar alanda kart
 * listesi. Satır / kart seçilince ayrıntı ve işlemler açılır.
 */
const WorkerList = ({ rows, wide, loading, totalCount, selectedId, onSelect, filters, onFilterChange, filtersActive, onClearFilters, onAdd }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`projectManagement.workers.${key}`, vars);

  if (loading) {
    return (
      <div aria-label={label('loading')} className="tw-space-y-2">
        {[0, 1, 2, 3, 4].map((index) => <span key={index} className="tw-block tw-h-12 tw-animate-pulse tw-rounded-xl tw-bg-slate-200/70 dark:tw-bg-white/[0.06]" />)}
      </div>
    );
  }

  if (totalCount === 0 || (rows.length === 0 && !wide)) {
    const noWorkers = totalCount === 0;
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-px-6 tw-py-10 tw-text-center">
        <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
          {noWorkers ? <LuUsers aria-hidden="true" className="tw-h-7 tw-w-7" /> : <LuSearchX aria-hidden="true" className="tw-h-7 tw-w-7" />}
        </span>
        <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{noWorkers ? label('empty') : label('noMatch')}</p>
        {noWorkers ? (
          <Button variant="primary" size="sm" onClick={onAdd}>
            <LuUserPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('addFirst')}
          </Button>
        ) : filtersActive && (
          <Button size="sm" onClick={onClearFilters}>{t('dataTable.clearAll')}</Button>
        )}
      </div>
    );
  }

  const selectOnKey = (event, id) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(id);
    }
  };

  if (!wide) {
    return (
      <ul className="tw-space-y-2">
        {rows.map(({ worker, summary }) => {
          const selected = selectedId === worker.id;
          return (
            <li key={worker.id} className="tw-block">
              <button
                type="button"
                onClick={() => onSelect(worker.id)}
                aria-current={selected || undefined}
                className={`tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-p-3 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                  selected
                    ? 'tw-border-teal-500/40 tw-bg-teal-500/[0.07] dark:tw-border-teal-400/30'
                    : 'tw-border-slate-200 tw-bg-white/60 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]'
                } ${summary.status === 'passive' ? 'tw-opacity-75' : ''}`}
              >
                <span aria-hidden="true" className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-violet-500/10 tw-text-sm tw-font-semibold tw-text-violet-700 dark:tw-text-violet-200">
                  {initialsOf(worker)}
                </span>
                <span className="tw-min-w-0 tw-flex-1 tw-space-y-1">
                  <span className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-min-w-0 tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{`${worker.name || ''} ${worker.surname || ''}`.trim()}</span>
                    <WorkerStatusBadge status={summary.status} />
                  </span>
                  <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    {summary.uniqueRoles.map((role) => roleLabel(t, role)).join(', ') || '—'}
                  </span>
                  <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    <span className="tw-inline-flex tw-items-center tw-gap-1"><LuBuilding2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5" /><CentersSummary summary={summary} /></span>
                    {worker.assignedAt && (
                      <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums"><LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />{formatAssignedDate(worker.assignedAt, locale)}</span>
                    )}
                  </span>
                </span>
                <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  const fields = workerFilterFields(t);
  const header = (key) => {
    const field = fields.find((item) => item.key === key);
    return (
      <th scope="col" className={HEAD}>
        <span className="tw-inline-flex tw-items-center tw-gap-1.5">
          {field.label}
          <ColumnFilterButton
            label={field.label}
            type={field.type}
            options={field.options}
            value={filters[key]}
            state={filters[key] ? 'applied' : 'off'}
            onChange={(value) => onFilterChange(key, value)}
          />
        </span>
      </th>
    );
  };

  return (
    <div className="tw-max-h-[min(34rem,58vh)] tw-overflow-auto tw-overscroll-contain tw-rounded-2xl tw-border tw-border-slate-200 glass-scrollbar dark:tw-border-white/10">
      <table className="tw-w-full tw-border-separate tw-border-spacing-0 tw-text-sm">
        <thead className="tw-bg-transparent">
          <tr className="tw-bg-transparent">
            {header('name')}
            {header('surname')}
            {header('role')}
            {header('center')}
            {header('assignedAt')}
            {header('status')}
          </tr>
        </thead>
        <tbody className="tw-bg-transparent">
          {rows.length === 0 ? (
            <tr className="tw-bg-transparent">
              <td colSpan={6} className="tw-px-6 tw-py-10 tw-text-center">
                <span className="tw-inline-flex tw-flex-col tw-items-center tw-gap-3">
                  <LuSearchX aria-hidden="true" className="tw-h-7 tw-w-7 tw-text-slate-400" />
                  <span className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{label('noMatch')}</span>
                  {filtersActive && <Button size="sm" onClick={onClearFilters}>{t('dataTable.clearAll')}</Button>}
                </span>
              </td>
            </tr>
          ) : rows.map(({ worker, summary }) => {
            const selected = selectedId === worker.id;
            return (
              <tr
                key={worker.id}
                tabIndex={0}
                aria-selected={selected}
                onClick={() => onSelect(worker.id)}
                onKeyDown={(event) => selectOnKey(event, worker.id)}
                className={`tw-cursor-pointer tw-text-slate-700 tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-teal-500/50 dark:tw-text-slate-200 ${
                  selected ? 'tw-bg-teal-500/[0.08] dark:tw-bg-teal-400/[0.08]' : 'tw-bg-transparent hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.04]'
                } ${summary.status === 'passive' ? 'tw-opacity-70' : ''}`}
              >
                <td className={`${CELL} tw-font-semibold tw-text-slate-900 dark:tw-text-white`} title={worker.name || ''}>
                  <span className={`tw-mr-2 tw-inline-block tw-h-4 tw-w-0.5 tw-rounded-full tw-align-middle ${selected ? 'tw-bg-teal-500' : 'tw-bg-transparent'}`} aria-hidden="true" />
                  {worker.name || ''}
                </td>
                <td className={CELL} title={worker.surname || ''}>{worker.surname || ''}</td>
                <td className={CELL}><RolesSummary roles={summary.uniqueRoles} /></td>
                <td className={CELL}><CentersSummary summary={summary} /></td>
                <td className={`${CELL} tw-tabular-nums`}>{formatAssignedDate(worker.assignedAt, locale)}</td>
                <td className={CELL}><WorkerStatusBadge status={summary.status} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

WorkerList.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({ worker: PropTypes.object, summary: PropTypes.object })).isRequired,
  wide: PropTypes.bool,
  loading: PropTypes.bool,
  totalCount: PropTypes.number.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  filtersActive: PropTypes.bool,
  onClearFilters: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default WorkerList;
